import { useAuth, useUser } from '@clerk/clerk-react';
import { env } from '@/lib/env';
import { assetUrl } from '@/lib/assets';

// Estado de sessão utilizável em QUALQUER componente, com ou sem ClerkProvider.
// Em preview (Tailscale sem Clerk) não há provider — chamar useAuth() rebentava.
// `env.preview` é constante durante toda a vida da app, por isso o ramo do hook
// nunca muda entre renders (regra dos hooks respeitada na prática).
export function useSignedIn(): { signedIn: boolean; ready: boolean } {
  // Harness de QA visual, MESMA convenção do `marc:qa:role` em services/access.ts
  // (que já existe para abrir os ecrãs guardados por permissão). Sem isto, o
  // preview é sempre "convidado" e metade da app — o QR pessoal, as ligações, as
  // conversas — não tem como ser inspecionada nem fotografada para os baselines.
  // Só existe atrás de `env.preview`; em produção este ramo nem é alcançável.
  if (env.preview) {
    const qa = typeof localStorage !== 'undefined' && localStorage.getItem('marc:qa:signedin') === '1';
    return { signedIn: qa, ready: true };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isSignedIn, isLoaded } = useAuth();
  return { signedIn: !!isSignedIn, ready: isLoaded };
}

/** Abre o sign-in do Clerk (modal) — no preview não há Clerk, não faz nada. */
export function openSignIn() {
  type ClerkGlobal = { openSignIn?: (opts?: Record<string, unknown>) => void };
  const clerk = (window as unknown as { Clerk?: ClerkGlobal }).Clerk;
  clerk?.openSignIn?.();
}

/**
 * Foto do utilizador para identificar as SUAS mensagens. A API (`avatar_url`) pode vir vazia (a foto
 * vive na conta de login e nem sempre é copiada para a BD); nesse caso usa-se a imagem da conta.
 * Em preview não há provider — só a da API. O ramo é constante durante a vida da app (regra dos hooks).
 */
export function useProfilePhoto(apiUrl?: string | null): string | null {
  if (env.preview) return assetUrl(apiUrl) ?? null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { user } = useUser();
  // `avatar_url` da API é um caminho RELATIVO (/uploads/avatars/…): sem `assetUrl` o browser resolve-o
  // contra o www (404) — foi o que deixou o chat só com iniciais em produção.
  return assetUrl(apiUrl) ?? user?.imageUrl ?? null;
}
